'use client'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { OrderBreakdown } from '@/app/[locale]/checkout/components/OrderBreakdown'
import { ButtonNextLink } from '@/components/ButtonNextLink'
import { CartTotal } from '@/components/CartTotal/CartTotal'
import { Pillow } from '@/components/Pillow/Pillow'
import type { ShopSession } from '@/services/shopSession/ShopSession.types'
import { useShopSession } from '@/services/shopSession/ShopSessionContext'
import type { RoutingLocale } from '@/utils/l10n/types'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { PageLink } from '@/utils/PageLink'
import { useResponsiveVariant } from '@/utils/useResponsiveVariant'
import * as Popover from '@radix-ui/react-popover'
import { Button } from 'ui/src/components/Button/Button'
import { Card } from 'ui/src/components/Card/Card'
import { Divider } from 'ui/src/components/Divider/Divider'
import { Text } from 'ui/src/components/Text/Text'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { ShoppingBagIcon } from './ShoppingBagIcon'
import { popoverContent, trigger, wrapper } from './ShoppingCartMenuItem.css'

export const SHOP_CART_MENU_ITEM_ID = 'shopping-cart-menu-item'

export function ShoppingCartMenuItem() {
  const { t } = useTranslation('cart')
  const locale = useRoutingLocale()
  const { shopSession } = useShopSession()
  const variant = useResponsiveVariant('lg')

  const cart = shopSession?.cart
  if (!cart) {
    return (
      <div id={SHOP_CART_MENU_ITEM_ID} className={wrapper}>
        <div className={trigger.default}>
          <ShoppingBagIcon count={0} />
        </div>
      </div>
    )
  }

  const cartLineCount = cart.items.length
  const hasItems = cartLineCount > 0
  const isDesktopWithItems = variant === 'desktop' && hasItems
  if (isDesktopWithItems) {
    return (
      <div id={SHOP_CART_MENU_ITEM_ID} className={wrapper}>
        <CartPopover cart={cart} locale={locale} />
      </div>
    )
  }

  return (
    <div id={SHOP_CART_MENU_ITEM_ID} className={wrapper}>
      <Link
        className={cartLineCount > 0 ? trigger.filled : trigger.default}
        href={PageLink.checkout({ locale }).pathname}
        aria-label={t('SHOP_CART_MENU_ARIA_LABEL', { count: cartLineCount })}
      >
        <ShoppingBagIcon count={cartLineCount} />
      </Link>
    </div>
  )
}

function CartPopover({ cart, locale }: { cart: NonNullable<ShopSession['cart']>; locale: RoutingLocale }) {
  const { t } = useTranslation('cart')
  const cartLineCount = cart.items.length

  return (
    <Popover.Root>
      <Popover.Trigger className={trigger.filled} aria-label={t('SHOP_CART_MENU_ARIA_LABEL', { count: cartLineCount })}>
        <ShoppingBagIcon count={cartLineCount} />
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content className={popoverContent} side="bottom" align="end" sideOffset={16} asChild={true}>
          <Card.Root>
            <Card.Header>
              <ul className={yStack({ gap: 'md' })}>
                {cart.items.map(({ productOffer }) => (
                  <li key={productOffer.id} className={xStack({ gap: 'sm' })}>
                    <Pillow
                      size="small"
                      src={productOffer.product.pillowImage.src}
                      alt={productOffer.product.pillowImage.alt}
                      role="presentation"
                    />
                    <div>
                      <Text as="p" size="md" color="textPrimary">
                        {productOffer.product.displayNameFull}
                      </Text>
                      <Text as="p" size="md" color="textSecondary">
                        {productOffer.product.name === 'SE_PAYMENT_PROTECTION' && productOffer.insuranceAmount
                          ? productOffer.insuranceAmount.displayName
                          : productOffer.exposure.displayNameShort}
                      </Text>
                    </div>
                  </li>
                ))}
              </ul>
            </Card.Header>

            <Card.Content className={yStack({ gap: 'md' })}>
              <Divider />
              <OrderBreakdown cart={cart} />
              <Divider />

              <CartTotal cost={cart.cost} />

              <div className={yStack({ gap: 'xs' })}>
                <Popover.Close asChild={true}>
                  <ButtonNextLink href={PageLink.checkout({ locale }).pathname} size="large" fullWidth={true}>
                    {t('CART_POPOVER_GO_TO_CHECKOUT_BUTTON')}
                  </ButtonNextLink>
                </Popover.Close>

                <Popover.Close asChild={true}>
                  <Button variant="secondary" size="large" fullWidth={true}>
                    {t('CART_POPOVER_CONTINUE_SHOPPING_BUTTON')}
                  </Button>
                </Popover.Close>
              </div>
            </Card.Content>
          </Card.Root>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
